<template>

<!--
	The archive: every past puzzle, newest first, as a grid of cards (No. + date,
	with a small check when the player has solved it). A card routes to
	/archive/:date, where Daily.vue replays it in unranked practice mode. A quiet
	banner reminds the player practice doesn't touch their streak. Chrome wraps it.
-->

<div class="archive">

	<app-topbar v-on:signin="$emit('signin')" />

	<main class="archive-body">

		<div class="archive-card">

			<h1 class="archive-title">Archive</h1>
			<p class="archive-banner">Practice mode: your streak isn't affected.</p>

			<div v-if="loading" class="archive-state">Loading…</div>

			<div v-else-if="!days.length" class="archive-empty">
				<app-mark :size="42" />
				<div class="archive-empty-head">No past puzzles yet</div>
				<p class="archive-empty-sub">Once a few days of puzzles have run, they’ll collect here to replay in practice mode. Start with today’s.</p>
				<router-link to="/" class="archive-empty-cta">Play today’s puzzle</router-link>
			</div>

			<div v-else class="archive-grid">
				<router-link
					v-for="day in days"
					:key="day.date"
					:to="'/archive/' + day.date"
					class="archive-day"
				>
					<div class="archive-thumb" aria-hidden="true">
						<span v-for="n in 16" :key="n" class="archive-thumb-tile"></span>
					</div>
					<div class="archive-day-foot">
						<div class="archive-day-meta">
							<div class="archive-day-no">#{{ day.number }}</div>
							<div class="archive-day-date">{{ day.date }}</div>
						</div>
						<span v-if="day.solved" class="archive-day-solved" aria-label="Solved">✓</span>
						<span v-else class="archive-day-unsolved" aria-hidden="true">—</span>
					</div>
					<!-- overall metrics across all players (shown even if unplayed) -->
					<div class="archive-day-metrics">
						<template v-if="day.metrics && day.metrics.plays">
							{{ day.metrics.plays }} played · {{ day.metrics.solve_rate }}% solved<template v-if="day.metrics.avg_tiles"> · avg {{ day.metrics.avg_tiles }} tiles</template>
						</template>
						<template v-else>No plays yet</template>
					</div>
				</router-link>
			</div>

		</div>

	</main>

	<app-foot />

</div>

</template>

<script>

import api from '../service/api'

export default {

	name: 'Archive',

	data: function() {

		return {
			days: [],
			loading: false
		}

	},

	created: function() {

		this.fetch()

	},

	methods: {

		fetch: function() {

			var self = this

			this.loading = true

			return api.request('archive', {}).then(function(json) {

				self.loading = false

				var days = (json && json.days) || []

				// newest first — sort defensively in case the server doesn't
				self.days = days.slice().sort(function(a, b) {

					return (b.date || '').localeCompare(a.date || '')

				})

			})

		}

	}

}

</script>

<style scoped>

.archive {
	display: flex;
	flex-direction: column;
	min-height: 100%;
	flex: 1;
}

.archive-body {
	flex: 1;
	display: flex;
	justify-content: center;
	padding: 20px 16px;
}

.archive-card {
	width: 100%;
	max-width: 440px;
}

.archive-title {
	font-family: var(--font-display);
	font-size: 26px;
	font-weight: 500;
	letter-spacing: -0.01em;
	margin: 0;
	color: var(--ink);
}

.archive-banner {
	font-family: var(--font-body);
	font-size: 13px;
	color: var(--muted);
	margin: 5px 0 18px;
}

.archive-state {
	padding: 28px 2px;
	font-family: var(--font-body);
	font-size: 14px;
	color: var(--muted);
	text-align: center;
}

/* empty state: mark + heading + blurb + CTA back to today's puzzle */
.archive-empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 40px 20px 36px;
}

.archive-empty-head {
	margin-top: 16px;
	font-family: var(--font-display);
	font-size: 20px;
	font-weight: 600;
	color: var(--ink);
}

.archive-empty-sub {
	margin: 7px auto 0;
	max-width: 320px;
	font-family: var(--font-body);
	font-size: 13.5px;
	line-height: 1.5;
	color: var(--muted-2);
}

.archive-empty-cta {
	margin-top: 18px;
	display: inline-flex;
	align-items: center;
	height: 42px;
	padding: 0 22px;
	border-radius: 8px;
	background: var(--ink);
	color: var(--paper);
	font-family: var(--font-body);
	font-size: 14px;
	font-weight: 600;
	text-decoration: none;
}

.archive-empty-cta:hover {
	background: var(--accent);
}

.archive-grid {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 12px;
}

.archive-day {
	display: block;
	border: 1px solid var(--hairline);
	border-radius: 10px;
	overflow: hidden;
	background: #fff;
	text-decoration: none;
	color: inherit;
	transition: border-color 140ms ease, box-shadow 140ms ease;
}

.archive-day:hover {
	border-color: var(--muted-2);
	box-shadow: var(--shadow);
}

.archive-thumb {
	height: 78px;
	background: repeating-linear-gradient(135deg, #cdbfa6 0 12px, #c4b69b 12px 24px);
	display: grid;
	grid-template-columns: repeat(4, 1fr);
	grid-template-rows: repeat(4, 1fr);
}

.archive-thumb-tile {
	border: 0.5px solid rgba(239, 233, 220, 0.5);
	background: #efe9dc;
	opacity: 0.55;
}

.archive-day-foot {
	padding: 10px 12px;
	display: flex;
	align-items: center;
	justify-content: space-between;
}

.archive-day-no {
	font-family: var(--font-mono);
	font-size: 12px;
	color: var(--ink);
}

.archive-day-date {
	font-family: var(--font-body);
	font-size: 11px;
	color: var(--muted);
	margin-top: 1px;
}

.archive-day-solved {
	width: 18px;
	height: 18px;
	border-radius: 50%;
	background: #eef1ea;
	border: 1px solid #c4d2bd;
	display: flex;
	align-items: center;
	justify-content: center;
	font-family: var(--font-mono);
	font-size: 10px;
	color: var(--correct);
	flex: none;
}

.archive-day-unsolved {
	font-family: var(--font-mono);
	font-size: 13px;
	color: var(--tile-2);
}

.archive-day-metrics {
	padding: 0 12px 10px;
	margin-top: -2px;
	font-family: var(--font-mono);
	font-size: 9.5px;
	letter-spacing: 0.02em;
	color: var(--muted);
}

@media (min-width: 720px) {

	.archive-card {
		max-width: 720px;
	}

	.archive-grid {
		grid-template-columns: repeat(3, 1fr);
	}

}

</style>
